import {
	ChairType,
	MaterialTheme,
	SeatingType,
	Side,
	isRoundTable,
	RoundTableType,
	TableType,
	Vec3,
} from "src/definitions/type-definitions";
import {
	chairWidth,
	circularRadius,
	longSideZ,
	makeChairSlot,
	padSlots,
	shortSideOffset,
	tableLength,
	ChairSlot,
} from "src/definitions/chair-data";
import type { Material } from "@definitions";

/* eslint-disable @animech/axiom/strict-function-syntax */

// ---------------------------------------------------------------------------
// Long-side position algorithm
// ---------------------------------------------------------------------------
const minChairGap = 0.05;

export function computeLongSidePositions(
	table: TableType,
	chair: SeatingType,
	count: number,
): [number, number, number, number] {
	const L = tableLength[table] ?? 1.7;
	const W = chairWidth[chair] ?? 0.6;
	const minStep = W + minChairGap;
	const step = count > 0 ? Math.max(L / (count + 1), minStep) : 0;
	const span = step * (count - 1);
	return [1, 2, 3, 4].map((i) =>
		count >= i ? -span / 2 + step * (i - 1) : 0,
	) as [number, number, number, number];
}

// ---------------------------------------------------------------------------
// Circular position algorithm
// ---------------------------------------------------------------------------
type CircularSlot = { position: Vec3; rotation: Vec3 };
type CircularPositions = [
	CircularSlot,
	CircularSlot,
	CircularSlot,
	CircularSlot,
	CircularSlot,
	CircularSlot,
];

const zeroSlot: CircularSlot = {
	position: { x: 0, y: 0, z: 0 },
	rotation: { x: 0, y: 0, z: 0 },
};

export function computeCircularPositions(
	table: RoundTableType,
	_chair: ChairType,
	count: number,
): CircularPositions {
	if (count <= 0) {
		return [zeroSlot, zeroSlot, zeroSlot, zeroSlot, zeroSlot, zeroSlot];
	}
	const r = circularRadius[table];
	const result: CircularSlot[] = [];
	for (let i = 0; i < 6; i++) {
		if (i < count) {
			const angleDeg = (360 / count) * i;
			const angleRad = (angleDeg * Math.PI) / 180;
			const x = r * Math.sin(angleRad);
			const z = r * Math.cos(angleRad);
			const rotY = (angleDeg + 180) % 360;
			result.push({
				position: { x, y: 0, z },
				rotation: { x: 0, y: rotY, z: 0 },
			});
		} else {
			result.push(zeroSlot);
		}
	}
	return result as CircularPositions;
}

// ---------------------------------------------------------------------------
// Build functions — assemble full chair arrays for rect/round layouts
// ---------------------------------------------------------------------------
export type ChairConfig = {
	chairType: SeatingType;
	chairMaterialWood: Material | undefined;
	chairMaterialMetal: Material;
	chairMaterialWeld: Material;
	chairMaterialEndcap: Material | undefined;
	chairMaterialScrewAtlas: Material;
	chairMaterialScrewPhillips: Material;
	sideId: Side;
	chairTheme: MaterialTheme;
};
export function buildRectChairs(
	table: TableType,
	topChair: ChairConfig,
	bottomChair: ChairConfig,
	leftChair: ChairConfig,
	rightChair: ChairConfig,
	topCount: number,
	bottomCount: number,
	leftCount: number,
	rightCount: number,
): ChairSlot[] {
	const slots: ChairSlot[] = [];
	const topXs = computeLongSidePositions(table, topChair.chairType, topCount);
	const topZ = longSideZ[topChair.chairType];
	for (let i = 0; i < topCount; i++) {
		slots.push(
			makeChairSlot(
				true,
				{ x: topXs[i], y: 0, z: -topZ },
				{ x: 0, y: 0, z: 0 },
				topChair.chairType,
				topChair.chairMaterialWood,
				topChair.chairMaterialMetal,
				topChair.chairMaterialWeld,
				topChair.chairMaterialEndcap,
				topChair.chairMaterialScrewAtlas,
				topChair.chairMaterialScrewPhillips,
				topChair.sideId,
				0,
				topChair.chairTheme,
			),
		);
	}
	const bottomXs = computeLongSidePositions(
		table,
		bottomChair.chairType,
		bottomCount,
	);
	const bottomZ = longSideZ[bottomChair.chairType];
	for (let i = 0; i < bottomCount; i++) {
		slots.push(
			makeChairSlot(
				true,
				{ x: bottomXs[i], y: 0, z: bottomZ },
				{ x: 0, y: 180, z: 0 },
				bottomChair.chairType,
				bottomChair.chairMaterialWood,
				bottomChair.chairMaterialMetal,
				bottomChair.chairMaterialWeld,
				bottomChair.chairMaterialEndcap,
				bottomChair.chairMaterialScrewAtlas,
				bottomChair.chairMaterialScrewPhillips,
				bottomChair.sideId,
				0,
				bottomChair.chairTheme,
			),
		);
	}
	const offset = shortSideOffset[table] ?? 1.05;
	for (let i = 0; i < leftCount; i++) {
		slots.push(
			makeChairSlot(
				true,
				{ x: -offset, y: 0, z: 0 },
				{ x: 0, y: 90, z: 0 },
				leftChair.chairType,
				leftChair.chairMaterialWood,
				leftChair.chairMaterialMetal,
				leftChair.chairMaterialWeld,
				leftChair.chairMaterialEndcap,
				leftChair.chairMaterialScrewAtlas,
				leftChair.chairMaterialScrewPhillips,
				leftChair.sideId,
				0,
				leftChair.chairTheme,
			),
		);
	}
	for (let i = 0; i < rightCount; i++) {
		slots.push(
			makeChairSlot(
				true,
				{ x: offset, y: 0, z: 0 },
				{ x: 0, y: -90, z: 0 },
				rightChair.chairType,
				rightChair.chairMaterialWood,
				rightChair.chairMaterialMetal,
				rightChair.chairMaterialWeld,
				rightChair.chairMaterialEndcap,
				rightChair.chairMaterialScrewAtlas,
				rightChair.chairMaterialScrewPhillips,
				rightChair.sideId,
				0,
				rightChair.chairTheme,
			),
		);
	}
	return padSlots(slots);
}

export function buildRoundChairs(
	table: TableType,
	chair: ChairType,
	count: number,
	chairMaterialWood: Material | undefined,
	chairMaterialMetal: Material,
	chairMaterialWeld: Material,
	chairMaterialEndcap: Material | undefined,
	chairMaterialScrewAtlas: Material,
	chairMaterialScrewPhillips: Material,
	sideId: Side = "round",
	chairTheme: MaterialTheme = "theme-teak-galvanized",
): ChairSlot[] {
	if (!isRoundTable(table) || count <= 0) {
		return padSlots([]);
	}
	const circular = computeCircularPositions(table, chair, count);
	const slots: ChairSlot[] = [];
	for (let i = 0; i < count; i++) {
		slots.push(
			makeChairSlot(
				true,
				circular[i].position,
				circular[i].rotation,
				chair,
				chairMaterialWood,
				chairMaterialMetal,
				chairMaterialWeld,
				chairMaterialEndcap,
				chairMaterialScrewAtlas,
				chairMaterialScrewPhillips,
				sideId,
				0,
				chairTheme,
			),
		);
	}
	return padSlots(slots);
}
